import type { LayerBulk } from '../core/params/bulk';
import type { ParamSchema, ParamValues } from '../core/params/types';
import { defaultValues } from '../core/params/types';
import { randomSeed } from '../core/rng';

/** Canvas composite operations exposed as layer blend modes. */
export type BlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'difference'
  | 'exclusion'
  | 'lighten'
  | 'darken'
  | 'color-dodge'
  | 'hard-light';

export const BLEND_MODES: { value: BlendMode; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'multiply', label: 'Multiplizieren' },
  { value: 'screen', label: 'Negativ multiplizieren' },
  { value: 'overlay', label: 'Überlagern' },
  { value: 'difference', label: 'Differenz' },
  { value: 'exclusion', label: 'Ausschluss' },
  { value: 'lighten', label: 'Aufhellen' },
  { value: 'darken', label: 'Abdunkeln' },
  { value: 'color-dodge', label: 'Farbig abwedeln' },
  { value: 'hard-light', label: 'Hartes Licht' },
];

/** Maps a blend mode onto the canvas `globalCompositeOperation` value. */
export function blendToComposite(mode: BlendMode): GlobalCompositeOperation {
  return mode === 'normal' ? 'source-over' : (mode as GlobalCompositeOperation);
}

/**
 * The technique id reserved for the text itself.
 *
 * The text is a layer like any other so it can sit anywhere in the stack — under the
 * effects, over them, or hidden entirely while still shaping the field.
 */
export const TEXT_TECHNIQUE_ID = '__text__';

export interface Layer {
  id: string;
  techniqueId: string;
  /** User-editable; defaults to the technique name. */
  name: string;
  visible: boolean;
  opacity: number;
  blendMode: BlendMode;
  /** Layers draw from their own deterministic stream, so one layer's changes do not
   *  reshuffle another's randomness. */
  seed: number;
  seedLocked: boolean;
  values: ParamValues;
  bulk: LayerBulk;
  collapsed: boolean;
}

let idCounter = 0;

export function createLayerId(prefix = 'layer'): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

export function createLayer(
  techniqueId: string,
  name: string,
  schema: ParamSchema,
  overrides: Partial<Layer> = {},
): Layer {
  return {
    id: createLayerId(techniqueId === TEXT_TECHNIQUE_ID ? 'text' : 'layer'),
    techniqueId,
    name,
    visible: true,
    opacity: 1,
    blendMode: 'normal',
    seed: randomSeed(),
    seedLocked: false,
    values: defaultValues(schema),
    bulk: {},
    collapsed: false,
    ...overrides,
  };
}

export function duplicateLayer(layer: Layer): Layer {
  return {
    ...layer,
    id: createLayerId(),
    name: `${layer.name} Kopie`,
    seed: randomSeed(),
    values: structuredClone(layer.values),
    bulk: structuredClone(layer.bulk),
  };
}
